/**
 * ReportPrintPage — Server-side print target for Playwright PDF generation.
 * Renders ALL report slides sequentially with page-break CSS.
 * 
 * When ?snapshot=true is provided: fetches cached data from MongoDB (fast path, ~5s).
 * When no snapshot: fetches data from APIs including AI generation (slow path, 2-3 min).
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { buildReportSlides } from '@/components/ReportPreview';

const API_DEFAULT = process.env.REACT_APP_BACKEND_URL;

export default function ReportPrintPage() {
  const { projectId } = useParams();
  const [data, setData] = useState(null);
  const [project, setProject] = useState(null);
  const [status, setStatus] = useState('Initialising...');

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const API = params.get('api') || API_DEFAULT;
  const useSnapshot = params.get('snapshot') === 'true';

  useEffect(() => {
    const token = localStorage.getItem('pulse_token');
    if (!projectId) { setStatus('No project ID'); return; }

    (async () => {
      try {
        if (useSnapshot) {
          /* ── SNAPSHOT PATH: use cached data from MongoDB ── */
          // Use relative URL so Playwright (localhost) bypasses Cloudflare
          setStatus('Loading cached report data...');
          const res = await fetch(`/api/projects/${projectId}/report-snapshot`);
          if (!res.ok) throw new Error(`Failed to fetch report snapshot: ${res.status}`);
          const { report_data, project_info } = await res.json();

          setData(report_data);
          setProject(project_info);
          setStatus('Rendering slides...');

          // Set document title for PDF metadata
          const projName = project_info?.name || 'Report';
          const today = new Date().toISOString().slice(0, 10);
          document.title = `Pulse - ${projName} - ${today}`;

          // Charts have isAnimationActive={false} so they render instantly.
          // Wait for ResponsiveContainer resize observers and image loads.
          setTimeout(() => {
            window.__REPORT_READY = true;
            setStatus('Ready');
          }, 5000);

        } else {
          /* ── SLOW PATH: fetch everything from APIs ── */
          if (!token) { setStatus('No token'); return; }
          const headers = { Authorization: `Bearer ${token}` };
          const get = (url) => fetch(`${API}${url}`, { headers }).then(r => { if (!r.ok) throw new Error(`${url}: ${r.status}`); return r.json(); });
          const post = (url) => fetch(`${API}${url}`, { method: 'POST', headers }).then(r => { if (!r.ok) throw new Error(`${url}: ${r.status}`); return r.json(); });

          setStatus('Fetching report data...');
          const [reportData, dashData, insightsData, publicStats, trendData, projData] = await Promise.all([
            get(`/api/projects/${projectId}/report-preview-data`),
            get(`/api/projects/${projectId}/executive-summary`),
            get(`/api/projects/${projectId}/key-insights`).catch(() => null),
            get(`/api/projects/${projectId}/stakeholders/public-stats`).catch(() => null),
            get(`/api/projects/${projectId}/trend-analysis`).catch(() => null),
            get(`/api/projects/${projectId}`).catch(() => null),
          ]);

          const segmentTypes = dashData?.segments_by_type || [];
          const segTrends = await Promise.all(
            segmentTypes.map(st =>
              get(`/api/projects/${projectId}/trend-analysis?segment_type_id=${st.id}`)
                .then(d => ({ id: st.id, name: st.name, data: d }))
                .catch(() => ({ id: st.id, name: st.name, data: null }))
            )
          );

          const dateStr = reportData.generated_at
            ? new Date(reportData.generated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
            : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

          reportData._dateFormatted = dateStr;
          reportData._dashSegmentsByType = segmentTypes;
          reportData._dashDriverSummaries = dashData?.driver_summaries || [];
          reportData._dashParticipation = dashData?.participation || {};
          reportData._dashOverallSentiment = dashData?.overall_sentiment;
          reportData._insights = insightsData;
          reportData._publicStats = publicStats;
          reportData._overallTrend = trendData;
          reportData._segmentTrends = segTrends;
          reportData._segmentAnalysis = [];

          setProject(projData);
          setData({ ...reportData });

          setStatus('Generating AI analysis (this may take 1-2 minutes)...');
          const aiPromises = [];

          aiPromises.push(
            post(`/api/projects/${projectId}/driver-report-insights`)
              .then(insights => {
                const di = [...(reportData.driver_insights || [])];
                (insights || []).forEach(ai => {
                  const idx = di.findIndex(d => d.driver_id === ai.driver_id || d.driver === ai.driver_name);
                  if (idx >= 0) di[idx] = { ...di[idx], insight: ai.insight };
                });
                reportData.driver_insights = di;
              })
              .catch(e => console.warn('Driver insights failed:', e))
          );

          (reportData.segment_type_groups || []).forEach(stg => {
            if (!stg.type_id) return;
            aiPromises.push(
              post(`/api/projects/${projectId}/segment-analysis?segment_type_id=${stg.type_id}`)
                .then(res => { reportData._segmentAnalysis = [...(reportData._segmentAnalysis || []), ...(res || [])]; })
                .catch(e => console.warn('Segment analysis failed:', e))
            );
          });

          aiPromises.push(
            post(`/api/projects/${projectId}/free-text-themes`)
              .then(aiThemes => {
                if (!aiThemes?.length) return;
                const di = [...(reportData.driver_insights || [])];
                di.forEach((driver, dIdx) => {
                  const qs = [...(driver.questions || [])];
                  qs.forEach((q, qi) => {
                    if (q.question_type === 'free_text') {
                      const m = aiThemes.find(a => a.question_id === q.id);
                      if (m) qs[qi] = { ...q, sentiment_breakdown: m.sentiment_breakdown, themes: m.themes };
                    }
                  });
                  di[dIdx] = { ...driver, questions: qs };
                });
                reportData.driver_insights = di;
              })
              .catch(e => console.warn('Free text themes failed:', e))
          );

          await Promise.all(aiPromises);
          setStatus('Rendering slides...');
          setData({ ...reportData });

          // Set document title for PDF metadata
          const projName = projData?.name || 'Report';
          const today = new Date().toISOString().slice(0, 10);
          document.title = `Pulse - ${projName} - ${today}`;

          setTimeout(() => {
            window.__REPORT_READY = true;
            setStatus('Ready');
          }, 5000);
        }
      } catch (err) {
        console.error('ReportPrintPage error:', err);
        setStatus(`Error: ${err.message}`);
        setTimeout(() => { window.__REPORT_READY = true; }, 5000);
      }
    })();
  }, [projectId, useSnapshot, API]);

  const slides = data ? buildReportSlides(data, project) : [];

  // Detect if this is a direct user visit (not Playwright)
  const isUserView = typeof window !== 'undefined' && !navigator.userAgent.includes('HeadlessChrome');

  return (
    <>
      <style>{`
        @page { size: A4 landscape; margin: 0; }
        html, body { margin: 0; padding: 0; background: white; }
        .print-slide {
          width: 1120px;
          height: 792px;
          overflow: hidden;
          position: relative;
          break-after: page;
          break-inside: avoid;
          page-break-after: always;
          page-break-inside: avoid;
        }
        .print-slide:last-child {
          break-after: auto;
          page-break-after: auto;
        }
        @media print {
          .status-banner, .print-toolbar { display: none !important; }
          body > *:not(#root) { display: none !important; }
          #root > *:not(.print-wrapper):not([class*="print-wrapper"]) { display: none !important; }
        }
      `}</style>

      {status !== 'Ready' && (
        <div className="status-banner" style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
          background: '#3D7A84', color: 'white', padding: '8px 16px',
          fontSize: 13, fontWeight: 500, textAlign: 'center',
        }}>
          {status}
        </div>
      )}

      {isUserView && status === 'Ready' && (
        <div className="print-toolbar" style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
          background: '#3D7A84', color: 'white', padding: '10px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
          fontSize: 14, fontWeight: 500,
        }}>
          <span>Report ready — {slides.length} pages</span>
          <button
            onClick={() => window.print()}
            style={{
              background: 'white', color: '#3D7A84', border: 'none', borderRadius: 6,
              padding: '6px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14,
            }}
          >
            Print / Save as PDF
          </button>
          <span style={{ fontSize: 12, opacity: 0.8 }}>
            Tip: In the print dialog, select "Save as PDF" and set Layout to "Landscape"
          </span>
        </div>
      )}

      <div className="print-wrapper" style={{ paddingTop: isUserView && status === 'Ready' ? 48 : 0 }}>
        {slides.map(slide => (
          <div key={slide.key} className="print-slide">
            {slide.el}
          </div>
        ))}
      </div>
    </>
  );
}
